import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';
import { Lock } from 'lucide-react';
import { cx, initialsOf } from './cx';
import { Button } from './Button';

// ─── Status ────────────────────────────────────────────────────────────────

export type StatusTone = 'ok' | 'amb' | 'acc' | 'muted' | 'info';

const TONE_CLASS: Record<StatusTone, string> = {
  ok: 'bg-ok-bg text-ok',
  amb: 'bg-amb-bg text-amb',
  acc: 'bg-acc-bg text-acc',
  muted: 'bg-fill2 text-t4',
  info: 'bg-info-bg text-info',
};

/** Maps the product's status words onto a tone, per the design brief. */
const STATUS_TONE: Record<string, StatusTone> = {
  confirmed: 'ok',
  ready: 'ok',
  published: 'ok',
  full: 'ok',
  approved: 'ok',
  pending: 'amb',
  queued: 'amb',
  draft: 'muted',
  declined: 'acc',
  failed: 'acc',
  unfilled: 'muted',
  analyzing: 'info',
  played: 'muted',
  done: 'muted',
};

export function StatusPill({
  status,
  tone,
  children,
  className,
}: {
  /** A status word ("Confirmed", "Queued"...) - also the label unless children are given. */
  status?: string;
  tone?: StatusTone;
  children?: ReactNode;
  className?: string;
}) {
  const t = tone ?? STATUS_TONE[(status || '').toLowerCase()] ?? 'muted';
  return (
    <span
      className={cx(
        'inline-flex items-center gap-[5px] text-[11.5px] font-semibold px-2.5 py-1 rounded-full shrink-0 whitespace-nowrap',
        TONE_CLASS[t],
        className,
      )}
    >
      {children ?? status}
    </span>
  );
}

/** "Read only" chip with a lock, for past services and history. */
export function LockedChip({ className }: { className?: string }) {
  return (
    <StatusPill tone="muted" className={cx('!text-t3', className)}>
      <Lock size={11} strokeWidth={2.4} aria-hidden="true" />
      Read only
    </StatusPill>
  );
}

// ─── People & keys ─────────────────────────────────────────────────────────

export function Avatar({
  name,
  size = 34,
  ring,
  className,
  style,
}: {
  name?: string | null;
  size?: 28 | 30 | 32 | 34 | 36 | 38 | 40 | 44 | 48 | 72;
  /** Border colour for stacked avatars ('bg' or 'card'). */
  ring?: 'bg' | 'card';
  className?: string;
  style?: CSSProperties;
}) {
  const fs = size >= 72 ? 22 : size >= 44 ? 13 : size >= 38 ? 12 : size >= 34 ? 11.5 : size >= 30 ? 10.5 : 10;
  return (
    <span
      aria-hidden="true"
      className={cx('rounded-full bg-avatar text-t2 font-bold flex items-center justify-center shrink-0', className)}
      style={{
        width: size,
        height: size,
        fontSize: fs,
        border: ring ? `2px solid var(--${ring})` : undefined,
        ...style,
      }}
    >
      {initialsOf(name)}
    </span>
  );
}

export function AvatarStack({ names, max = 5, size = 28, ring = 'bg' }: { names: string[]; max?: number; size?: 28 | 32; ring?: 'bg' | 'card' }) {
  const shown = names.slice(0, max);
  const more = names.length - shown.length;
  return (
    // role="img": the initials are decorative, the label carries the names.
    <div className="flex pl-1.5" role="img" aria-label={names.join(', ')}>
      {shown.map((n, i) => (
        <Avatar key={`${n}-${i}`} name={n} size={size} ring={ring} style={{ marginLeft: -6 }} />
      ))}
      {more > 0 && (
        <span
          className="rounded-full bg-fill2 text-t3 text-[11px] font-semibold flex items-center px-2"
          style={{ height: size, marginLeft: -6, border: `2px solid var(--${ring})` }}
        >
          +{more}
        </span>
      )}
    </div>
  );
}

export function KeyBadge({
  k,
  tone = 'neutral',
  size = 'md',
  className,
}: {
  k?: string | null;
  tone?: 'neutral' | 'accent' | 'danger';
  /** sm 32×28 (tables), md 42 square (rows), lg 52 (cards). */
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  return (
    <span
      className={cx(
        'font-mono font-bold inline-flex items-center justify-center shrink-0',
        tone === 'neutral' && 'bg-fill2 text-t1',
        tone !== 'neutral' && 'bg-acc-bg text-acc',
        size === 'sm' && 'min-w-[32px] h-7 px-1 rounded-[8px] text-[13px]',
        size === 'md' && 'w-[42px] h-[42px] rounded-[12px] text-[14px]',
        size === 'lg' && 'w-[52px] h-[52px] rounded-[14px] text-[22px]',
        className,
      )}
      aria-label={k ? `Key ${k}` : 'Key unknown'}
    >
      {k || '–'}
    </span>
  );
}

// ─── Layout ────────────────────────────────────────────────────────────────

export function Card({
  children,
  className,
  pad = 22,
  as: Tag = 'div',
  style,
  ...rest
}: HTMLAttributes<HTMLElement> & { pad?: number | string; as?: 'div' | 'section' | 'form' }) {
  return (
    <Tag
      className={cx('rounded-[24px] border border-line bg-card', className)}
      style={{ padding: pad, ...style }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/** Eyebrow + H1 on the left, actions on the right; wraps on narrow screens. */
export function PageHeader({
  eyebrow,
  title,
  actions,
  description,
  className,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  actions?: ReactNode;
  description?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx('flex flex-wrap items-end justify-between gap-4 mb-[22px]', className)}>
      <div className="min-w-0 flex-[1_1_320px]">
        {eyebrow && <div className="text-[13px] text-t4 mb-2 flex items-center gap-2">{eyebrow}</div>}
        <h1 className="wp-h1">{title}</h1>
        {description && <div className="text-[14.5px] text-t3 mt-2 leading-[1.6] wp-pretty">{description}</div>}
      </div>
      {actions && <div className="flex flex-wrap gap-2 items-center">{actions}</div>}
    </div>
  );
}

/** "IN PROGRESS (2)" style eyebrow with an optional count chip. */
export function SectionLabel({ children, count, className }: { children: ReactNode; count?: number; className?: string }) {
  return (
    <div className={cx('wp-eyebrow flex items-center gap-2', className)}>
      {children}
      {count !== undefined && <span className="font-mono px-[7px] py-0.5 rounded-full bg-fill2">{count}</span>}
    </div>
  );
}

/** Pill filter chip (tag filters, name chips). */
export function Chip({
  on,
  onClick,
  children,
  size = 34,
}: {
  on: boolean;
  onClick: () => void;
  children: ReactNode;
  size?: 30 | 34;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cx(
        'wp-press rounded-full border font-medium cursor-pointer whitespace-nowrap',
        on ? 'bg-t1 text-bg border-transparent' : 'bg-fill text-t3 border-line hover:text-t1',
        size === 30 ? 'h-[30px] px-3 text-[12.5px]' : 'h-[34px] px-3.5 text-[13px]',
      )}
    >
      {children}
    </button>
  );
}

// ─── States ────────────────────────────────────────────────────────────────

export function Meter({ value, limit, className, label }: { value: number; limit: number; className?: string; label?: string }) {
  const pct = limit > 0 ? Math.min(100, Math.round((value / limit) * 100)) : 0;
  const color = value >= limit ? 'var(--acc)' : value >= limit * 0.85 ? 'var(--amb)' : 'var(--ok)';
  return (
    <div
      role="meter"
      aria-valuemin={0}
      aria-valuemax={limit}
      aria-valuenow={value}
      aria-label={label}
      className={cx('h-1.5 rounded-full bg-fill2 overflow-hidden', className)}
    >
      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color, transition: 'width .5s var(--ease)' }} />
    </div>
  );
}

export function ProgressBar({ pct, tone = 'acc', className }: { pct: number; tone?: 'acc' | 'info' | 'ok'; className?: string }) {
  return (
    <div className={cx('h-1.5 rounded-full bg-fill2 overflow-hidden', className)} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: `var(--${tone})`, transition: 'width .3s ease' }} />
    </div>
  );
}

/** Indeterminate 3px bar for "analyzing". */
export function IndeterminateBar({ className }: { className?: string }) {
  return (
    <span className={cx('relative block h-[3px] rounded-full bg-fill2 overflow-hidden', className)} aria-hidden="true">
      <span
        className="absolute top-0 bottom-0 w-1/3 rounded-full bg-acc"
        style={{ animation: 'wpBar 1.4s ease-in-out infinite' }}
      />
    </span>
  );
}

export function EmptyState({
  title,
  body,
  action,
  dashed = true,
  className,
}: {
  title: ReactNode;
  body?: ReactNode;
  action?: ReactNode;
  dashed?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cx(
        'px-6 py-[60px] text-center rounded-[24px]',
        dashed ? 'border-[1.5px] border-dashed border-line2' : '',
        className,
      )}
    >
      <div className="text-[17px] font-semibold">{title}</div>
      {body && <div className="text-[14px] text-t4 mt-1.5 mb-[18px] wp-pretty max-w-[440px] mx-auto">{body}</div>}
      {action}
    </div>
  );
}

export function ErrorState({
  title = "Something didn't load",
  body,
  onRetry,
  retrying,
  className,
}: {
  title?: ReactNode;
  body?: ReactNode;
  onRetry?: () => void;
  retrying?: boolean;
  className?: string;
}) {
  return (
    <div
      role="alert"
      className={cx('wp-rise rounded-[20px] border bg-acc-bg p-5 flex flex-wrap items-center gap-3.5', className)}
      style={{ borderColor: 'rgba(208,48,69,.3)' }}
    >
      <div className="flex-[1_1_260px]">
        <div className="text-[15px] font-semibold">{title}</div>
        {body && <div className="text-[13.5px] text-t2 mt-1 leading-normal">{body}</div>}
      </div>
      {onRetry && (
        <Button variant="ghost" onClick={onRetry} loading={retrying} className="!bg-card">
          Try again
        </Button>
      )}
    </div>
  );
}

export function SkeletonRows({ rows = 3, className }: { rows?: number; className?: string }) {
  return (
    <div className={cx('flex flex-col gap-3', className)} aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3.5 p-4 rounded-[18px] border border-line bg-card">
          <span className="wp-skeleton w-[42px] h-[42px] rounded-[12px]" />
          <div className="flex-1 flex flex-col gap-2">
            <span className="wp-skeleton h-3 w-3/5 rounded-[6px]" />
            <span className="wp-skeleton h-2.5 w-[35%] rounded-[6px]" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Coloured message bar (amber warnings, red errors, green success). */
export function Banner({
  tone,
  children,
  className,
  role,
}: {
  tone: 'amb' | 'acc' | 'ok' | 'info' | 'muted';
  children: ReactNode;
  className?: string;
  role?: 'status' | 'alert';
}) {
  return (
    <div
      role={role}
      className={cx(
        'px-3.5 py-3 rounded-[12px] text-[13.5px] leading-normal',
        tone === 'amb' && 'bg-amb-bg text-amb',
        tone === 'acc' && 'bg-acc-bg text-acc',
        tone === 'ok' && 'bg-ok-bg text-ok',
        tone === 'info' && 'bg-info-bg text-info',
        tone === 'muted' && 'bg-fill text-t3',
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Small grey stat tile ("Original key", "Weekly services"). */
export function StatTile({ label, value, mono = true, className }: { label: ReactNode; value: ReactNode; mono?: boolean; className?: string }) {
  return (
    <div className={cx('bg-fill rounded-[12px] px-3.5 py-3 min-w-0', className)}>
      <div className="text-[12px] text-t4">{label}</div>
      <div className={cx('text-[18px] font-bold mt-[3px] truncate', mono && 'font-mono')}>{value}</div>
    </div>
  );
}
